/** @typedef {{ simulatorKey: string, canonicalName: string, aliases: string[] }} TechMappingRow */
/** @typedef {{ name: string, gw: number }} UnmappedTech */

export const SIMULATOR_GW_KEYS = [
  'solarGw',
  'windOnGw',
  'windOffGw',
  'nuclearGw',
  'hydroGw',
  'coalGw',
  'gasGw',
  'oilGw',
  'bioGw',
  'geoGw',
  'batteryStorageGw',
  'pumpedStorageGw'
];

/** Spaces → underscores; lowercase for case-insensitive matching. */
export function normalizeTechNameForMatch(name) {
  if (name == null) return '';
  return String(name).trim().replace(/\s+/g, '_').toLowerCase();
}

function aliasIsWildcard(alias) {
  return String(alias).includes('*');
}

function wildcardToRegExp(normalizedPattern) {
  const escaped = normalizedPattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp(`^${escaped}$`, 'i');
}

function aliasMatchesNormalizedName(normalizedName, alias) {
  const normAlias = normalizeTechNameForMatch(alias);
  if (!normAlias) return false;
  if (aliasIsWildcard(alias)) {
    return wildcardToRegExp(normAlias).test(normalizedName);
  }
  return normalizedName === normAlias;
}

/**
 * @param {string} normalizedName
 * @param {TechMappingRow[]} rows
 * @returns {string|null} simulatorKey
 */
export function matchTechNameToSimulatorKey(normalizedName, rows) {
  if (!normalizedName || !Array.isArray(rows) || rows.length === 0) return null;

  let bestExactKey = null;
  let bestExactLen = -1;

  for (const row of rows) {
    for (const alias of row.aliases || []) {
      if (aliasIsWildcard(alias)) continue;
      const normAlias = normalizeTechNameForMatch(alias);
      if (normalizedName === normAlias && normAlias.length > bestExactLen) {
        bestExactLen = normAlias.length;
        bestExactKey = row.simulatorKey;
      }
    }
  }
  if (bestExactKey) return bestExactKey;

  const wildcardOrder = [
    'pumpedStorageGw',
    'solarGw',
    'windOnGw',
    'windOffGw',
    'nuclearGw',
    'coalGw',
    'gasGw',
    'oilGw',
    'bioGw',
    'geoGw',
    'batteryStorageGw',
    'hydroGw'
  ];

  for (const key of wildcardOrder) {
    const row = rows.find((r) => r.simulatorKey === key);
    if (!row) continue;
    for (const alias of row.aliases || []) {
      if (!aliasIsWildcard(alias)) continue;
      if (aliasMatchesNormalizedName(normalizedName, alias)) {
        return row.simulatorKey;
      }
    }
  }

  return null;
}

/**
 * @param {Record<string, number>} capacity — raw tech name → GW
 * @param {TechMappingRow[]} mappingRows
 * @returns {{ mapped: Record<string, number>, unmapped: UnmappedTech[] }}
 */
export function mapRawCapacityToSimulatorGws(capacity, mappingRows) {
  const mapped = {};
  const unmapped = [];

  if (!capacity || typeof capacity !== 'object') {
    return { mapped, unmapped };
  }

  for (const [originalName, rawGw] of Object.entries(capacity)) {
    const gw = typeof rawGw === 'number' ? rawGw : parseFloat(rawGw);
    if (!Number.isFinite(gw)) continue;

    const normalized = normalizeTechNameForMatch(originalName);
    const simulatorKey = matchTechNameToSimulatorKey(normalized, mappingRows);

    if (simulatorKey) {
      mapped[simulatorKey] = (mapped[simulatorKey] || 0) + gw;
    } else {
      unmapped.push({ name: originalName, gw });
    }
  }

  return { mapped, unmapped };
}

/**
 * Build flat native v1 payload from raw scenario + mapped GW sums.
 * @param {object} rawScenario — parsed URL with format raw
 * @param {Record<string, number>} mappedGws
 */
export function rawMappedToNativePayload(rawScenario, mappedGws) {
  const pickShared = (key) => {
    const v = rawScenario?.[key];
    if (v === undefined || v === null || v === '') return undefined;
    return v;
  };

  const out = { v: 1 };
  for (const key of SIMULATOR_GW_KEYS) {
    if (mappedGws[key] !== undefined) {
      out[key] = mappedGws[key];
    }
  }
  const demand = pickShared('demandTwh');
  if (demand !== undefined) out.demandTwh = demand;
  const wy = pickShared('weatherYear');
  if (wy !== undefined) out.weatherYear = wy;
  const swy = pickShared('supplyWeatherYear');
  if (swy !== undefined) out.supplyWeatherYear = swy;
  const bd = pickShared('batteryDurationHours');
  if (bd !== undefined) out.batteryDurationHours = bd;
  const pd = pickShared('pumpedDurationHours');
  if (pd !== undefined) out.pumpedDurationHours = pd;

  return out;
}

export function isRawScenarioFormat(parsed) {
  return parsed && typeof parsed === 'object' && parsed.format === 'raw';
}

/** Raw URLs only: omitted fossil keys default to 0 (not DB capacity). */
export const RAW_FOSSIL_ZERO_DEFAULT_KEYS = ['coalGw', 'gasGw', 'oilGw'];

/**
 * After mapping, inject explicit zeros for fossil keys not present in mapped output.
 * @param {object} flatNative
 */
export function applyRawFossilZeroDefaults(flatNative) {
  const out = { ...flatNative };
  for (const key of RAW_FOSSIL_ZERO_DEFAULT_KEYS) {
    if (out[key] === undefined) {
      out[key] = 0;
    }
  }
  return out;
}

/** Unmapped entries with GW > 0 for the warning banner. */
export function unmappedTechsForBanner(unmapped) {
  if (!Array.isArray(unmapped)) return [];
  return unmapped.filter((u) => Number.isFinite(u.gw) && u.gw > 0);
}
